/**
 * CobroQuickDialog (Phase QR2 refactor)
 * --------------------------------------
 * Diálogo modal para registrar un cobro rápido desde la agenda o desde
 * cualquier listado de pacientes. La lógica está dividida en:
 *
 *  - `lib/cobroQuickHelpers.js`  — funciones puras (matching, validación, total).
 *  - `lib/useCobroQuickForm.js`  — hook que encapsula estado y carga de pacientes.
 *  - `components/cobros/CobroFormFields.jsx` — layout del formulario.
 *
 * Este componente se queda únicamente como orquestador (diálogo + submit).
 * Complejidad ciclomática reducida de 27 a ~5.
 */
import React, { useMemo } from 'react';
import { api, formatEUR } from '../lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';
import { useCobroQuickForm } from '../lib/useCobroQuickForm';
import { computeCobroDesglose, signedCobroAmount, validateCobroForm } from '../lib/cobroQuickHelpers';
import { canonicalCobroConcept, isDevolucionConcept } from '../lib/cobroPresets';
import { CobroFormFields } from './cobros/CobroFormFields';

export default function CobroQuickDialog({
  open, onOpenChange, onSaved,
  prefillName = '', defaultConcept = '',
  prefillPatient = null,
  defaultAmount = null, defaultPaymentMethod = '',
  agendaId = null, agendaItemRef = null,
}) {
  const { patients, form, setField, selectPatient } = useCobroQuickForm({
    open, prefillName, prefillPatient, defaultConcept,
    defaultAmount, defaultPaymentMethod,
  });
  const [busy, setBusy] = useState(false);

  // Devolución a mano (2026-09-08): en negativo y sin IVA.
  const esDevolucion = isDevolucionConcept(form.concept);
  // Lo escrito es lo que paga el paciente; con IVA, el 21% va DENTRO
  // (24/9/2026). El desglose es la misma cuenta del servidor.
  const desglose = useMemo(
    () => computeCobroDesglose(form.amount, form.vat_applied, form.concept),
    [form.amount, form.vat_applied, form.concept]
  );
  const total = desglose.total;

  const submit = async (e) => {
    e?.preventDefault?.();
    const errorMsg = validateCobroForm(form);
    if (errorMsg) {
      toast.error(errorMsg);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        concept: canonicalCobroConcept(form.concept),
        amount: signedCobroAmount(form.amount, form.concept),
        vat_applied: esDevolucion ? false : form.vat_applied,
        // El importe lleva el IVA dentro: sin esta marca el servidor rechaza
        // el cobro con IVA (pestañas de antes del 24/9/2026).
        iva_dentro: true,
        patient_id: form.patient_id || null,
        agenda_id: agendaId || null,
        agenda_item_ref: agendaItemRef || null,
      };
      const res = await api.post('/api/cobros', payload);
      toast.success(`${esDevolucion ? 'Devolución registrada' : 'Cobro registrado'} · ${formatEUR(res.data.total)}`);
      if (onSaved) onSaved(res.data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-[#2E6E5E]" />
            {esDevolucion ? 'Registrar devolución' : 'Registrar cobro'}
            {agendaItemRef && (
              <span className="text-xs font-normal text-muted-foreground">· {agendaItemRef}</span>
            )}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3" data-testid="cobro-quick-dialog">
          <CobroFormFields
            form={form}
            patients={patients}
            setField={setField}
            selectPatient={selectPatient}
          />

          <div className="flex items-center justify-between pt-2 border-t border-border">
            <div className="text-sm text-muted-foreground" data-testid="cobro-quick-desglose">
              {esDevolucion
                ? 'Devolución: resta de la caja, sin IVA'
                : form.vat_applied
                  ? (
                    <>
                      Base imponible <span className="tabular-nums" data-testid="cobro-quick-base">{formatEUR(desglose.base)}</span>
                      {' · '}IVA (21%) <span className="tabular-nums" data-testid="cobro-quick-iva">{formatEUR(desglose.iva)}</span>
                      <span className="block text-xs">IVA incluido en el importe</span>
                    </>
                  )
                  : 'Sin IVA'}
            </div>
            <div className={`text-xl font-display font-semibold tabular-nums ${esDevolucion ? 'text-red-700' : 'text-[#13231E]'}`} data-testid="cobro-quick-total">
              Total: {formatEUR(total)}
            </div>
          </div>

          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={busy}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
              data-testid="cobro-quick-submit"
            >
              {busy
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</>
                : (esDevolucion ? 'Registrar devolución' : 'Registrar cobro')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
